import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import styled from 'styled-components';

import {
  cioFacetsToUrlFilters,
  cioSizeFacetsToFilterPreferences,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import useGender from '~/shared/utils/useGender';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import useProductGridData from '../../hooks/useProductGridData';
import {
  clearAllCioFacetsFromUrlParams,
  getSelectedCioFacetRbid,
  getTransformedFacetOptionDisplayName,
  isCioSizeFacet,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll';

const DynamicFormattedMessage = FormattedMessage;

const DesktopGridSelectedFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Heading = styled.p`
  font-size: 1rem;
  ${({ theme }) => theme.gridFilterStyles.headerStyle};
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  text-decoration: underline;
  font-family: inherit;
  ${({ theme }) => theme.gridFilterStyles.clearAllButtonStyle};
`;

const FilterList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 1rem 0;
`;

const FilterItem = styled.li`
  display: flex;
  align-items: center;
  line-height: 16px;
  padding: 4px 8px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  ${({ theme }) => theme.gridFilterStyles.tagStyle};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersTextColor};
  cursor: pointer;
  background: none;
  font-family: inherit;
  ${({ theme }) => theme.typography.variants.gridEmphasized.textStyle};
`;

const Icon = styled(MdClose)`
  stroke-width: 2;
  margin-top: 0px;
  color: ${({ theme }) => theme.colors.gridSelectedFiltersPillIconColor};
`;

const DesktopGridSelectedFilters = ({ closeAllPanels }) => {
  const { selectedUrlFilters, allCioFacetNames, allCioFacets } =
    useProductGridData();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const domain = useDomain();
  const { gender } = useGender();

  const selectedCioFacets = Object.entries(selectedUrlFilters).flatMap(
    ([key, value]) => {
      const cioFacetName = urlFiltersToCioFacets[key] || key;
      const cioFacet = allCioFacets?.find(
        (facet) => facet?.name === cioFacetName
      );

      if (cioFacet) {
        return value
          ?.map((option) => {
            const categoryName =
              cioSizeFacetsToFilterPreferences[cioFacet.name];

            const translatedName = sizeByKey({
              domain,
              gender,
              category: categoryName,
              key: option,
            });
            const cioOption = cioFacet?.options?.find(
              (facetOption) => facetOption?.value === option
            );

            if (!cioOption) {
              return null;
            }

            return {
              facetName: cioFacetName,
              urlName: key,
              facetOption: translatedName || option,
              facetOptionDisplayName: getTransformedFacetOptionDisplayName({
                selectedFacetName: cioFacetName,
                selectedFacetDisplayName: cioFacet?.display_name,
                selectedFacetOptionDisplayName: cioOption?.display_name,
              }),
            };
          })
          ?.filter(Boolean);
      }
      return [];
    }
  );

  const handleClearAllFilters = () => {
    clearAllCioFacetsFromUrlParams({
      router,
      allCioFacetNames,
    });
    closeAllPanels();
    scrollToTopOfGrid({ isMobile });
  };

  const handleOnClick = ({ facetName, facetOption }) => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  if (!selectedCioFacets?.length) {
    return null;
  }

  return (
    <DesktopGridSelectedFiltersContainer>
      <HeaderRow>
        <Heading>
          <FormattedMessage
            id="site_product_filter.filtered_by"
            defaultMessage="Filtered by:"
          />
        </Heading>
        <ClearAllButton
          type="button"
          onClick={handleClearAllFilters}
          aria-label="Clear all filters"
          data-autotag="filter-clear-all"
        >
          <FormattedMessage
            id="site_product_filter.clear_all"
            defaultMessage="Clear all"
          />
        </ClearAllButton>
      </HeaderRow>
      <FilterList data-autotag="filtered-by-textbox">
        {selectedCioFacets?.map(
          ({ facetName, facetOption, facetOptionDisplayName }) => (
            <FilterItem
              key={`${facetName}-${facetOption}`}
              data-autotag="filtered-by-option"
            >
              {isCioSizeFacet(facetName) ? (
                <>
                  <DynamicFormattedMessage
                    id={`site_product_filter.${facetOption}`}
                    defaultMessage={facetOption}
                  />
                  {' ('}
                  <DynamicFormattedMessage
                    id={`site_product_filter.${
                      cioFacetsToUrlFilters[facetName] || facetName
                    }`}
                    defaultMessage={
                      cioFacetsToUrlFilters[facetName] || facetName
                    }
                  />
                  )
                </>
              ) : (
                <DynamicFormattedMessage
                  id={`site_product_filter.${getSelectedCioFacetRbid(
                    facetName,
                    facetOptionDisplayName
                  )}`}
                  defaultMessage={facetOptionDisplayName}
                />
              )}
              <RemoveButton
                type="button"
                onClick={() =>
                  handleOnClick({
                    facetName,
                    facetOption,
                  })
                }
                aria-label={`Remove ${facetOptionDisplayName} filter`}
                data-autotag="filtered-by-option-close-icon"
              >
                <Icon size={12} />
              </RemoveButton>
            </FilterItem>
          )
        )}
      </FilterList>
    </DesktopGridSelectedFiltersContainer>
  );
};

DesktopGridSelectedFilters.propTypes = {
  closeAllPanels: PropTypes.func.isRequired,
};

export default DesktopGridSelectedFilters;
